import { useEffect, useState } from "react";
import { useAccountApi } from "../api/accountApi";

// #2075 — resolve a Cognito sub (the "setBy" on a ring-state row,
// docs/score/ring-state.md) to a display name. Same account-common call and
// response envelope PeopleManager.jsx's scribe-role load already uses (#736):
// GET /org/{orgId}/member via useAccountApi().org.listOrganizationMembers.
// Keyed by userId so a caller can resolve a sub in O(1) and RingStateSection
// never has a raw sub left to fall back to — an unresolved id (a former
// member, a service-written row, or this call failing outright) resolves to
// `{}`/a miss, and the caller supplies the neutral fallback.
//
// limit: 100 (the API's max, apps/account-api/src/handlers/orgmember.js)
// rather than the 25 default — ring-state setters are whoever has used this
// dashboard, not just scribes, so under-fetching the member page would
// silently reintroduce the raw-GUID bug for larger orgs.
export function useOrgMembersById(orgId) {
  const accountApi = useAccountApi();
  const [membersById, setMembersById] = useState({});

  useEffect(() => {
    if (!orgId) {
      setMembersById({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await accountApi.org.listOrganizationMembers(orgId, 100);
        const members =
          res.data?.status === "success" && Array.isArray(res.data.data?.members)
            ? res.data.data.members
            : [];
        if (!cancelled) {
          setMembersById(Object.fromEntries(members.map((m) => [m.userId, m])));
        }
      } catch {
        // A failed member lookup must never surface a raw sub — an empty map
        // hits the same neutral-fallback path as any other unresolved id.
        if (!cancelled) setMembersById({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, accountApi]);

  return membersById;
}

export default useOrgMembersById;
